import { defineMessage, defineMessages } from 'react-intl';
import { ProductProps } from '../../../types/products';
import { Plan } from '../../../types/plans';
//This file primarily stores helper functions for matrixPlanSelector and matrixColumns

/**
 * Returns the plan for a given product ID and interval length.
 * @param {number} productID - The ID of the product to get the plan for.
 * @param {Plan[]} plans - The list of plans to search through.
 * @param {number} planLength - The length of the plan interval.
 * @returns {Plan | undefined} - The plan for the given product ID and interval length, or undefined if not found.
 */
export function getProductPlan(
  productID: number,
  plans: Plan[],
  planLength: number,
): Plan | undefined {
  return plans
    .filter((plan) => plan.productID === productID)
    .find((plan) => plan.intervalLength === planLength);
}

/**
 * Returns the upsell plan for a selected plan.
 */
export const getUpsellProductPlan = (
  allPlans: Plan[],
  currentPlan?: Plan,
): Plan | undefined => {
  if (!allPlans?.length || !currentPlan || currentPlan?.code?.includes('suite'))
    return;
  const upsellPlan = allPlans
    .filter((plan) => plan.intervalLength === currentPlan.intervalLength)
    .find((plan) => plan.code.includes('suite'));
  return upsellPlan;
};

const getEnabled = (name: string) => (product: ProductProps) => {
  const nav = getSchemaProps(product, 'navs')?.primary?.find(
    ({ groupName }: { groupName: string }) => groupName === name, // 'Basic Sciences',);
  );
  return nav?.state === 'active';
};

const getSchemaProps = (product: ProductProps, schema: string) => {
  return (
    product.data?.find(({ schema: productSchema }) => productSchema === schema)
      ?.props || []
  );
};

const hasQuestions =
  (path: string, name?: string) => (product: ProductProps) => {
    const prop = getSchemaProps(product, 'quizbuilder').find(
      (prop: { path: string }) => prop.path === path,
    );
    return (
      getSchemaProps(product, 'productComponents').includes('quizbuilder') &&
      prop &&
      prop.name === name
    );
  };

export const features = [
  {
    title: (product: ProductProps) => {
      const titleMap = defineMessages({
        MD: {
          id: 'plans.selector.basic-sciences.md',
          defaultMessage:
            'Basic Sciences Videos (Foundational Sciences, Organ Systems, USMLE® Step 1 Review)',
        },
        DO: {
          id: 'plans.selector.basic-sciences.do_comlex',
          defaultMessage:
            'Basic Sciences Videos (Foundational Sciences, Organ Systems, COMLEX-USA Level 1 and USMLE® Step 1 Review)',
        },
        OH: {
          id: 'plans.selector.basic-sciences.oh',
          defaultMessage:
            'Basic Sciences Videos (Foundational Sciences, Organ Systems)',
        },
        PA: {
          id: 'plans.selector.basic-sciences.do',
          defaultMessage:
            'Basic Sciences Videos (Foundational Sciences, Organ Systems)',
        },
      });
      return titleMap[product.program as keyof typeof titleMap];
    },
    isEnabled: getEnabled('Basic Sciences'),
  },
  {
    title: (product: ProductProps) => {
      const titleMap = defineMessages({
        MD: {
          id: 'plans.selector.clinical-sciences.md',
          defaultMessage:
            'Clinical Sciences Videos (Clerkships, USMLE® Step 2 Review)',
        },
        DO: {
          id: 'plans.selector.clinical-sciences.do_comlex',
          defaultMessage:
            'Clinical Sciences Videos (Clerkships, COMLEX-USA® Level 2/USMLE® Step 2- Style Review)',
        },
        OH: {
          id: 'plans.selector.clinical-sciences.oh',
          defaultMessage: 'Clinical Sciences Videos (Clinical Rotations)',
        },
        PA: {
          id: 'plans.selector.clinical-sciences.do',
          defaultMessage:
            'Clinical Sciences Videos (Clinical Rotations, PANCE® Review)',
        },
      });
      return titleMap[product.program as keyof typeof titleMap];
    },
    isEnabled: getEnabled('Clinical Sciences'),
  },
  {
    title: defineMessage({
      id: 'plans.selector.notes',
      defaultMessage: 'OMM Notes',
    }),
    isEnabled: (product: ProductProps) => {
      return product.program === 'DO' && product.name !== 'Clinical Sciences';
    },
  },
  {
    title: defineMessage({
      id: 'plans.selector.step1.questions',
      defaultMessage: 'USMLE® Step 1-Style Questions',
    }),
    isEnabled: hasQuestions('step1'),
  },
  {
    title: defineMessage({
      id: 'plans.selector.step2.questions',
      defaultMessage: 'USMLE® Step 2-Style Questions',
    }),
    isEnabled: hasQuestions('step2'),
  },
  {
    title: defineMessage({
      id: 'plans.selector.comlex1.questions',
      defaultMessage: 'COMLEX-USA® Level 1/USMLE® Step 1- Style Questions',
    }),
    isEnabled: hasQuestions('step1-level1'),
  },
  {
    title: defineMessage({
      id: 'plans.selector.comlex2.questions',
      defaultMessage: 'COMLEX-USA® Level 2/USMLE® Step 2- Style Questions',
    }),
    isEnabled: hasQuestions('step2-level2'),
  },
  {
    title: defineMessage({
      id: 'plans.selector.omm.questions',
      defaultMessage: 'OMM questions for the COMLEX-USA®',
    }),
    isEnabled: (product: ProductProps) => {
      return product.program === 'DO' && product.name !== 'Clinical Sciences';
    },
  },
  {
    title: defineMessage({
      id: 'plans.selector.foundational.questions',
      defaultMessage: 'Foundational Questions',
    }),
    isEnabled: hasQuestions('step1', 'Foundational Questions'),
  },
  {
    title: defineMessage({
      id: 'plans.selector.preclinical.questions',
      defaultMessage: 'Preclinical Questions',
    }),
    isEnabled: hasQuestions('step1', 'Preclinical Questions'),
  },
  {
    title: defineMessage({
      id: 'plans.selector.clinical.questions',
      defaultMessage: 'Clinical Questions',
    }),
    isEnabled: hasQuestions('step2', 'Clinical Questions'),
  },
  {
    title: defineMessage({
      id: 'plans.selector.pance.questions',
      defaultMessage: 'PANCE-Style Questions',
    }),
    isEnabled: (product: ProductProps) => {
      if (product.name === 'Basic Sciences') return false;
      return hasQuestions('pance')(product);
    },
  },
  {
    title: defineMessage({
      id: 'plans.selector.dmt',
      defaultMessage: 'Decision-Making Trees',
    }),
    isEnabled: getEnabled('Clinical Sciences'),
  },
  {
    title: defineMessage({
      id: 'plans.selector.flashcards',
      defaultMessage: 'Flashcards',
    }),
    isEnabled: (product: ProductProps) => {
      return (
        ['MD', 'DO', 'OH', 'PA'].includes(product.program) &&
        product.name !== 'Clinical Sciences'
      );
    },
  },
  {
    title: defineMessage({
      id: 'plans.selector.hyn',
      defaultMessage: 'High-Yield Notes for Pathology and Physiology',
    }),
    isEnabled: (product: ProductProps) => {
      return product.name !== 'Clinical Sciences';
    },
  },
  {
    title: defineMessage({
      id: 'plans.selector.quizbuilder',
      defaultMessage: 'Quiz Builder',
    }),
    isEnabled: (product: ProductProps) => {
      return getSchemaProps(product, 'productComponents').includes(
        'quizbuilder',
      );
    },
  },
  {
    title: defineMessage({
      id: 'plans.selector.decks',
      defaultMessage: 'Decks - Build your own flashcards',
    }),
    isEnabled: (product: ProductProps) => {
      return getSchemaProps(product, 'productComponents').includes('decks');
    },
  },
  {
    title: defineMessage({
      id: 'plans.selector.playlists',
      defaultMessage: 'Playlists',
    }),
    isEnabled: (product: ProductProps) => {
      return true;
    },
  },
  {
    title: defineMessage({
      id: 'plans.selector.analytics',
      defaultMessage: 'Analytics',
    }),
    isEnabled: (product: ProductProps) => {
      return true;
    },
  },
  {
    title: defineMessage({
      id: 'plans.selector.mobile',
      defaultMessage: 'Mobile App',
    }),
    isEnabled: (product: ProductProps) => {
      return true;
    },
  },
  {
    title: defineMessage({
      id: 'plans.selector.spanish',
      defaultMessage: 'Available in Spanish',
    }),
    isEnabled: (product: ProductProps) => {
      return getSchemaProps(product, 'productComponents').includes(
        'languageSelection',
      );
    },
  },
];
